import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SiteHeader } from "@/components/site/SiteHeader";
import { useInsurer } from "@/context/InsurerContext";
import { insApi } from "@/lib/insurance";
import { errMsg } from "@/lib/api";
import { BRAND_PHOTOS } from "@/lib/constants";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#F4F4F4]" data-testid={testId}>
    <div className="relative hidden lg:block bg-[#020165] am-grain overflow-hidden">
      <img src={BRAND_PHOTOS[1] || BRAND_PHOTOS[0]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020165] to-[#020165]/50" />
      <div className="relative p-8 h-full flex flex-col"><SiteHeader />
        <div className="mt-auto text-white max-w-md pb-6">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#F86900]"><ShieldCheck className="h-4 w-4" />Insurer Portal</p>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight">Claims assessments and repair quotes, in one place.</h2>
          <p className="mt-3 text-white/70">Submit a request, follow every status change and approve Accelary quotes online.</p>
        </div>
      </div>
    </div>
    <div className="flex flex-col">
      <div className="lg:hidden p-4 bg-[#020165]"><SiteHeader /></div>
      {children}
    </div>
  </div>
);

export function InsurerLogin() {
  const { login } = useInsurer();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(f.email, f.password);
      const next = sp.get("next") || "";
      nav(next.startsWith("/insurer") ? next : "/insurer", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Could not sign in"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell testId="insurer-login-page">
      <form onSubmit={submit} className="m-auto w-full max-w-sm p-6 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F86900]">Insurer Portal</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0D141A]">Insurer sign in</h1>
        <Input required type="email" placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="insurer-login-email-input" />
        <Input required type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="insurer-login-password-input" />
        <button disabled={busy} data-testid="insurer-login-submit-button" className="w-full rounded-full bg-[#020165] text-white font-semibold py-3 hover:bg-[#0a0a8a] disabled:opacity-60 transition-colors">
          {busy ? "Please wait…" : "Sign in"}
        </button>
        <p className="text-xs text-[#56585E] text-center">Accounts are created by Accelary Motors. Need access? <Link to="/contact" className="underline">Contact us</Link></p>
        <p className="text-xs text-center"><Link to="/login" className="text-[#56585E] underline" data-testid="insurer-login-customer-link">Back to client login</Link></p>
      </form>
    </Shell>
  );
}

export function InsurerSetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await insApi.post("/auth/set-password", { token: sp.get("token") || "", password: f.password });
      toast.success("Password set. You can now sign in.");
      nav("/insurer/login");
    } catch (err) {
      toast.error(errMsg(err, "Could not set password"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell testId="insurer-set-password-page">
      <form onSubmit={submit} className="m-auto w-full max-w-sm p-6 space-y-4">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0D141A]">Choose your password</h1>
        <Input required type="password" minLength={8} placeholder="New password (min 8 characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="insurer-set-password-input" />
        <Input required type="password" placeholder="Confirm password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="insurer-set-password-confirm-input" />
        <button disabled={busy} data-testid="insurer-set-password-submit" className="w-full rounded-full bg-[#020165] text-white font-semibold py-3 disabled:opacity-60">{busy ? "Saving…" : "Set password"}</button>
      </form>
    </Shell>
  );
}
